import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Building2, Train } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { BranchMeta } from "@/content/locations/meta.gen";
import { REGION_LABEL_TH, KIND_LABEL_TH } from "@/content/locations/labels";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

const REGION_ORDER = [
  "bangkok",
  "vicinity",
  "central",
  "east",
  "north",
  "northeast",
  "south",
  "overseas",
];

export const Route = createFileRoute("/locations/")({
  loader: async (): Promise<{
    itemList: Record<string, unknown>;
    branches: BranchMeta[];
    counts: { offices: number; total: number; meetingPoints: number };
  }> => {
    const [{ buildLocationsItemList }, meta] = await Promise.all([
      import("@/lib/schema-locations"),
      import("@/content/locations/meta.gen"),
    ]);
    return {
      itemList: buildLocationsItemList() as Record<string, unknown>,
      branches: meta.BRANCH_META,
      counts: {
        offices: meta.BRANCH_OFFICE_COUNT,
        total: meta.BRANCH_TOTAL_COUNT,
        meetingPoints: meta.MEETING_POINT_COUNT,
      },
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: "จุดให้บริการทั่วไทย & ต่างประเทศ | Thai Notary",
      description:
        "สารบัญจุดให้บริการ Notary Public แปลรับรอง และนิติกรณ์กงสุล ครอบคลุมกรุงเทพฯ ปริมณฑล ทุกภาค สนามบิน ด่านชายแดน และเดสก์พันธมิตรต่างประเทศ",
      path: "/locations",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/locations")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "จุดให้บริการ", url: `${site}/locations` },
            ]),
          ),
        },
        ...(loaderData?.itemList
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(loaderData.itemList),
              },
            ]
          : []),
      ],
    };
  },
  component: LocationsIndex,
});

function LocationsIndex() {
  const { branches, counts } = Route.useLoaderData() as {
    branches: BranchMeta[];
    counts: { offices: number; total: number; meetingPoints: number };
  };
  const offices = counts.offices;
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto max-w-6xl px-4 py-12">
        <h1 className="text-3xl font-bold md:text-4xl">
          จุดให้บริการทั่วไทยและต่างประเทศ
        </h1>
        <p className="mt-4 max-w-3xl text-muted-foreground">
          เครือข่ายของเราแบ่งเป็น 2 ประเภทตามข้อกำหนดของ Google Business Profile
          อย่างเคร่งครัด — <strong>สำนักงานที่มีเจ้าหน้าที่ประจำ</strong> ({offices} แห่ง)
          ซึ่งประกาศที่อยู่จริงได้ และ <strong>พื้นที่ให้บริการ/จุดนัดพบ</strong>
          {" "}({counts.total - offices} จุด + {counts.meetingPoints} จุดนัดพบตามสถานีและศูนย์การค้า)
          ซึ่งทีมเดินทางไปพบลูกค้า ไม่ใช่หน้าร้าน
        </p>

        {REGION_ORDER.map((region) => {
          const items = branches.filter((b) => b.region === region);
          if (!items.length) return null;
          return (
            <section key={region} className="mt-10">
              <h2 className="mb-4 text-xl font-semibold">
                {REGION_LABEL_TH[region] ?? region}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((b) => (
                  <Link
                    key={b.slug}
                    to="/locations/$branch"
                    params={{ branch: b.slug }}
                    className="rounded-lg border border-border bg-card p-4 transition hover:border-primary"
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {b.isOffice ? (
                        <Building2 className="h-4 w-4" />
                      ) : (
                        <MapPin className="h-4 w-4" />
                      )}
                      {KIND_LABEL_TH[b.kind] ?? b.kind}
                    </div>
                    <div className="mt-1 font-medium">{b.nameTh}</div>
                    <div className="text-sm text-muted-foreground">{b.nameEn}</div>
                    {b.transitTop && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                        <Train className="h-3 w-3" />
                        {b.transitTop}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </main>
      <SiteFooter />
    </div>
  );
}
